import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useGPS } from '../../contexts/GPSContext';
import { Button } from '../../components/ui/button';
import CameraCapture from '../../components/CameraCapture';
import QRScanner from '../../components/QRScanner';
import PilotLoadmanButton from './PilotLoadmanButton';
import {
  SignIn, SignOut, Coffee, ForkKnife, UserCircle, Briefcase, BowlFood,
  Truck, PersonSimple, MapPin, QrCode, Camera, NavigationArrow, Pencil
} from '@phosphor-icons/react';

const BREAK_TYPES = [
  { key: 'breakfast', label: 'breakfast', icon: Coffee },
  // Tea (15 min). Pilots may take multiple tea breaks per day —
  // backend only blocks if another break is already in progress.
  { key: 'tea', label: 'tea', icon: Coffee },
  { key: 'lunch', label: 'lunch', icon: ForkKnife },
  { key: 'dinner', label: 'dinner', icon: BowlFood },
  { key: 'permission', label: 'permission', icon: UserCircle },
  { key: 'mgmt_work', label: 'mgmt_work', icon: Briefcase },
];

export default function PilotDashboard() {
  const { t } = useTranslation();
  const { user, checkAuth } = useAuth();
  const [attendance, setAttendance] = useState(null);
  const [breaks, setBreaks] = useState([]);
  const [activeBreak, setActiveBreak] = useState(null);
  const [loading, setLoading] = useState({});
  const [showQR, setShowQR] = useState(false);
  const [showSecondaryQR, setShowSecondaryQR] = useState(false);
  const [secondaryVehicle, setSecondaryVehicle] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [haltDialog, setHaltDialog] = useState(false);
  const [haltOptions, setHaltOptions] = useState([]);
  const [haltInput, setHaltInput] = useState('');
  const [haltSaving, setHaltSaving] = useState(false);
  const [myLoadmen, setMyLoadmen] = useState([]);

  // Phase 66: KM is now captured at vehicle scan (start) and leave (end),
  // NOT per trip. These dialogs hold the two flows.
  //   scanKmDialog: { qrData } — shown right after QR success to collect start KM
  //   leaveKmDialog: { open } — shown right before leaving to collect end KM
  const [scanKmDialog, setScanKmDialog] = useState(null);
  const [scanKmValue, setScanKmValue] = useState('');
  const [scanKmPhoto, setScanKmPhoto] = useState('');
  const [scanKmCamera, setScanKmCamera] = useState(false);
  const [scanSubmitting, setScanSubmitting] = useState(false);

  const [leaveKmDialog, setLeaveKmDialog] = useState(false);
  const [leaveKmValue, setLeaveKmValue] = useState('');
  const [leaveKmPhoto, setLeaveKmPhoto] = useState('');
  const [leaveKmCamera, setLeaveKmCamera] = useState(false);
  const [leaveSubmitting, setLeaveSubmitting] = useState(false);
  const gps = useGPS();

  const fetchStatus = useCallback(async () => {
    try {
      const [att, brk, dash] = await Promise.all([
        api.get('/pilot/attendance'),
        api.get('/pilot/breaks'),
        api.get('/pilot/dashboard'),
      ]);
      setAttendance(att.data);
      setBreaks(brk.data);
      setActiveBreak(brk.data.find(b => !b.end_time));
      setVehicleInfo(dash.data?.vehicle || null);
      setSecondaryVehicle(dash.data?.secondary_vehicle || null);
    } catch {}
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Auto-resume GPS when already checked in
  useEffect(() => {
    const isIn = attendance && attendance.check_in && !attendance.check_out;
    if (isIn && !gps.tracking) gps.start();
  }, [attendance]); // eslint-disable-line react-hooks/exhaustive-deps

  const isCheckedInDerived = attendance && attendance.check_in && !attendance.check_out;
  useEffect(() => {
    if (!isCheckedInDerived) { setMyLoadmen([]); return; }
    api.get('/pilot/my-loadmen').then(({ data }) => {
      setMyLoadmen(Array.isArray(data) ? data : []);
    }).catch(() => setMyLoadmen([]));
  }, [isCheckedInDerived]);

  const submitLeaveWithKm = async () => {
    const km = parseInt(leaveKmValue, 10);
    if (!km || km <= 0) {
      alert('Please enter a valid End KM reading.');
      return;
    }
    setLeaveSubmitting(true);
    try {
      const { data } = await api.post('/pilot/leave-vehicle', {
        end_km: km,
        end_km_photo: leaveKmPhoto || '',
      });
      setVehicleInfo(null);
      fetchStatus();
      setLeaveKmDialog(false);
      setLeaveKmValue('');
      setLeaveKmPhoto('');
      const sk = data?.session_kms;
      alert(sk ? `Vehicle left. Session: ${sk} KM.` : 'Vehicle left.');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to leave vehicle.');
    } finally {
      setLeaveSubmitting(false);
    }
  };

  const handleAction = async (action, data = {}) => {
    setLoading(prev => ({...prev, [action]: true}));
    try {
      if (action === 'check-in') {
        await api.post('/pilot/check-in');
        gps.start();
      }
      else if (action === 'check-out') {
        await api.post('/pilot/check-out');
        // GPS tracking automatically stops when the page unmounts
      }
      else if (action === 'leave-vehicle') await api.post('/pilot/leave-vehicle');
      else if (action === 'back-to-office') await api.post('/pilot/back-to-office');
      else if (action === 'move-without-vehicle') await api.post('/pilot/move-without-vehicle');
      else if (action.startsWith('break-start-')) await api.post('/pilot/break/start', { break_type: action.replace('break-start-', ''), gps: gps.lastPos || {} });
      else if (action === 'break-end') await api.post('/pilot/break/end', { gps: gps.lastPos || {} });
      fetchStatus();
    } catch (err) {
      alert(err.response?.data?.detail || 'Action failed');
    } finally {
      setLoading(prev => ({...prev, [action]: false}));
    }
  };

  const openHaltDialog = useCallback(async () => {
    setHaltInput(user?.halt_location || '');
    setHaltDialog(true);
    try {
      const { data } = await api.get('/pilot/halt-locations');
      setHaltOptions(Array.isArray(data) ? data : []);
    } catch {
      setHaltOptions([]);
    }
  }, [user?.halt_location]);

  // Open halt dialog when the layout's header "Change" button is pressed.
  useEffect(() => {
    const handler = () => openHaltDialog();
    window.addEventListener('open-halt-dialog', handler);
    return () => window.removeEventListener('open-halt-dialog', handler);
  }, [openHaltDialog]);

  const saveHaltLocation = async () => {
    const name = (haltInput || '').trim();
    if (!name) return;
    setHaltSaving(true);
    try {
      await api.put('/pilot/halt-location', { halt_location: name });
      setHaltDialog(false);
      fetchStatus();
      // Refresh user from /auth/me so the layout strip reflects the new halt.
      try { await checkAuth(); } catch {}
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save halt location');
    } finally {
      setHaltSaving(false);
    }
  };

  const handleQRScan = async (qrData) => {
    // Phase 66: Don't call the backend yet — collect Start KM + photo first,
    // then send everything together so scan is atomic and can't leave a half-
    // assigned vehicle record.
    setShowQR(false);
    setScanKmValue('');
    setScanKmPhoto('');
    setScanKmDialog({ qrData });
  };

  const submitScanWithKm = async () => {
    if (!scanKmDialog) return;
    const km = parseInt(scanKmValue, 10);
    if (!km || km <= 0) {
      alert('Please enter a valid Start KM reading.');
      return;
    }
    setScanSubmitting(true);
    try {
      const { data } = await api.post('/pilot/scan-vehicle', {
        qr_data: scanKmDialog.qrData,
        start_km: km,
        start_km_photo: scanKmPhoto || '',
      });
      setVehicleInfo(data.vehicle);
      fetchStatus();
      setScanKmDialog(null);
      alert(`Vehicle ${data.vehicle?.vehicle_number} assigned. Start KM ${km} recorded.`);
    } catch (err) {
      alert(err.response?.data?.detail || 'QR scan failed');
    } finally {
      setScanSubmitting(false);
    }
  };

  const handleSecondaryQRScan = async (qrData) => {
    setShowSecondaryQR(false);
    try {
      const { data } = await api.post('/pilot/scan-secondary-vehicle', { qr_data: qrData });
      setSecondaryVehicle(data.vehicle);
      fetchStatus();
      alert(`Secondary vehicle ${data.vehicle?.vehicle_number} added.\nIt won't receive auto-allocation; you can manually shift trips to it.`);
    } catch (err) {
      alert(err.response?.data?.detail || 'QR scan failed');
    }
  };

  const handleRemoveSecondary = async () => {
    if (!secondaryVehicle) return;
    if (!window.confirm(`Remove secondary vehicle ${secondaryVehicle.vehicle_number}?`)) return;
    try {
      await api.post('/pilot/leave-secondary-vehicle');
      setSecondaryVehicle(null);
      fetchStatus();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const isCheckedIn = attendance && attendance.check_in && !attendance.check_out;
  const isCheckedOut = attendance && attendance.check_out;

  return (
    <div data-testid="pilot-dashboard" className="space-y-6">
      {/* QR Scanner Overlay */}
      {showQR && <QRScanner onScan={handleQRScan} onClose={() => setShowQR(false)} />}
      {showSecondaryQR && <QRScanner onScan={handleSecondaryQRScan} onClose={() => setShowSecondaryQR(false)} />}

      {/* Camera Overlay */}
      {showCamera && (
        <CameraCapture
          label={t('take_photo')}
          onCapture={async (captureData) => {
            try {
              await api.post('/upload/photo-base64', {
                photo: captureData.photo,
                gps: captureData.gps,
                label: 'pilot_photo'
              });
            } catch {}
          }}
          onClose={() => setShowCamera(false)}
        />
      )}

      {/* Active break / status indicator (compact) */}
      {(activeBreak || isCheckedIn || isCheckedOut) && (
        <div className="text-xs text-[#666] flex flex-wrap items-center gap-2">
          {isCheckedIn && <span className="text-[#00C853] font-medium">{t('checked_in')}</span>}
          {isCheckedOut && <span className="text-[#FF3333]">{t('checked_out')}</span>}
          {!isCheckedIn && !isCheckedOut && <span className="text-[#808080]">{t('not_checked_in')}</span>}
          {activeBreak && <span className="text-[#FF8C00]">| {t('currently_on')} {t(activeBreak.break_type)}</span>}
        </div>
      )}

      {/* Change Halt Location Dialog */}
      {haltDialog && (
        <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-4" data-testid="halt-dialog">
          <div className="bg-white w-full max-w-md p-6 border border-[#E5E5E5]">
            <h3 className="text-lg font-black tracking-tight">Change Halt Location</h3>
            <p className="text-xs text-[#666] mt-1">Pick an existing halt or type a new one — it will be saved for everyone.</p>
            <div className="mt-4 space-y-3">
              <input
                type="text"
                value={haltInput}
                onChange={e => setHaltInput(e.target.value)}
                list="halt-options"
                placeholder="e.g. Carloo HO"
                className="w-full border border-[#E5E5E5] px-3 py-2 text-sm rounded-full focus:outline-none focus:border-[#002FA7]"
                data-testid="halt-input"
                autoFocus
              />
              <datalist id="halt-options">
                {haltOptions.map(h => <option key={h} value={h} />)}
              </datalist>
              {haltOptions.length > 0 && (
                <div className="max-h-40 overflow-y-auto border border-[#F3F3F3]">
                  {haltOptions.map(h => (
                    <button
                      key={h}
                      type="button"
                      onClick={() => setHaltInput(h)}
                      className={`w-full text-left text-sm px-3 py-2 border-b border-[#F3F3F3] last:border-b-0 hover:bg-[#002FA7]/5 ${haltInput === h ? 'bg-[#002FA7]/10 font-medium' : ''}`}
                      data-testid={`halt-option-${h}`}
                    >
                      {h}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setHaltDialog(false)}
                className="py-2 border border-[#E5E5E5] text-sm uppercase tracking-widest hover:bg-[#F3F3F3]"
                data-testid="halt-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={haltSaving || !haltInput.trim()}
                onClick={saveHaltLocation}
                className="py-2 bg-[#002FA7] text-white text-sm uppercase tracking-widest hover:bg-[#002FA7]/90 disabled:opacity-50"
                data-testid="halt-save"
              >
                {haltSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GPS bar removed — status now shows as a colored compass icon next to the language switcher in the header. */}

      {/* My Helpers (visible only while checked in) */}
      {isCheckedIn && (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {myLoadmen.length > 0 ? (
              <div className="flex flex-wrap gap-1.5" data-testid="my-loadmen-chips">
                <p className="text-[10px] uppercase tracking-widest text-[#666] w-full">My Helpers ({myLoadmen.length})</p>
                {myLoadmen.map(l => (
                  <span key={l.loadman_id} className="text-[10px] px-2 py-0.5 bg-[#002FA7]/10 text-[#002FA7] font-medium" title={`${l.name} · ${l.emp_id || '—'}`}>
                    {l.name}{l.emp_id ? ` (${l.emp_id})` : ''}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-[10px] uppercase tracking-widest text-[#999]">No loadmen added yet</p>
            )}
          </div>
          <PilotLoadmanButton assigned={myLoadmen} onChange={setMyLoadmen} />
        </div>
      )}

      {/* Check In/Out */}
      <div className="grid grid-cols-2 gap-4">
        <Button
          data-testid="check-in-btn"
          disabled={isCheckedIn || loading['check-in']}
          onClick={() => handleAction('check-in')}
          className={`h-16 text-white rounded-full text-sm uppercase tracking-widest font-medium ${isCheckedIn ? 'bg-[#00C853]/40' : 'bg-[#00C853] hover:bg-[#00C853]/90 hover:text-white'}`}
        >
          <SignIn size={20} className="mr-2" /> {isCheckedIn ? 'Checked In' : t('check_in')}
        </Button>
        <Button
          data-testid="check-out-btn"
          disabled={!isCheckedIn || loading['check-out']}
          onClick={() => handleAction('check-out')}
          className="h-16 bg-[#FF3333] text-white hover:bg-[#FF3333]/90 hover:text-white rounded-full text-sm uppercase tracking-widest font-medium"
        >
          <SignOut size={20} className="mr-2" /> {t('check_out')}
        </Button>
      </div>

      {/* Vehicle Actions */}
      {isCheckedIn && (
        <div className="space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-widest text-[#666]">{t('vehicle_actions')}</h3>

          <div className="grid grid-cols-3 gap-3">
            <button
              data-testid="scan-vehicle-btn"
              onClick={() => setShowQR(true)}
              className={`p-4 flex flex-col items-center justify-center gap-1 transition-colors min-h-[80px] ${
                vehicleInfo?.vehicle_number
                  ? 'bg-[#00C853] text-white hover:bg-[#00C853]/90'
                  : 'bg-[#002FA7] text-white hover:bg-[#002FA7]/90'
              }`}
            >
              {vehicleInfo?.vehicle_number ? (
                <>
                  <span className="text-base font-black tracking-tight font-mono leading-none" data-testid="pilot-current-vehicle">
                    {vehicleInfo.vehicle_number}
                  </span>
                  <span className="text-[9px] uppercase tracking-widest text-white/85 leading-none">Tap to change</span>
                </>
              ) : (
                <>
                  <QrCode size={28} weight="bold" />
                  <span className="text-xs uppercase tracking-widest text-center" data-testid="pilot-no-vehicle">{t('scan_vehicle')}</span>
                </>
              )}
            </button>
            <button
              data-testid="back-to-office-btn"
              onClick={() => handleAction('back-to-office')}
              disabled={user?.can_self_add_trip === false}
              title={user?.can_self_add_trip === false ? 'Self trip creation is disabled by admin' : ''}
              className={`bg-white border border-[#E5E5E5] p-4 flex flex-col items-center gap-2 transition-colors min-h-[80px] ${user?.can_self_add_trip === false ? 'opacity-50 cursor-not-allowed' : 'hover:border-[#002FA7]'}`}
            >
              <MapPin size={24} className="text-[#002FA7]" />
              <span className="text-xs uppercase tracking-widest text-center">{t('back_to_office')}</span>
            </button>
            <button
              data-testid="move-without-vehicle-btn"
              onClick={() => handleAction('move-without-vehicle')}
              disabled={user?.can_self_add_trip === false}
              title={user?.can_self_add_trip === false ? 'Self trip creation is disabled by admin' : ''}
              className={`bg-white border border-[#E5E5E5] p-4 flex flex-col items-center gap-2 transition-colors min-h-[80px] ${user?.can_self_add_trip === false ? 'opacity-50 cursor-not-allowed' : 'hover:border-[#808080]'}`}
            >
              <PersonSimple size={24} className="text-[#808080]" />
              <span className="text-xs uppercase tracking-widest text-center">{t('move_without_vehicle')}</span>
            </button>
          </div>

          {/* Secondary vehicle */}
          {vehicleInfo?.vehicle_number && (
            <div className="border border-dashed border-[#E5E5E5] rounded-2xl p-3" data-testid="secondary-vehicle-card">
              <p className="text-[10px] uppercase tracking-widest text-[#666] font-bold">Secondary Vehicle (manual allocation only)</p>
              {secondaryVehicle ? (
                <div className="flex items-center justify-between mt-2 gap-2">
                  <div>
                    <p className="text-base font-black tracking-tight font-mono text-[#0A0A0A]" data-testid="pilot-secondary-vehicle">
                      {secondaryVehicle.vehicle_number}
                    </p>
                    {secondaryVehicle.vehicle_id_manual && (
                      <p className="text-[10px] text-[#666] font-mono">{secondaryVehicle.vehicle_id_manual}</p>
                    )}
                  </div>
                  <button
                    onClick={handleRemoveSecondary}
                    data-testid="remove-secondary-btn"
                    className="px-3 py-2 text-[10px] uppercase tracking-widest border border-[#FF3333]/30 text-[#FF3333] rounded-full hover:bg-[#FF3333]/5"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowSecondaryQR(true)}
                  data-testid="add-secondary-vehicle-btn"
                  className="mt-2 w-full flex items-center justify-center gap-2 py-3 rounded-full bg-[#0A0A0A] text-white text-xs uppercase tracking-widest font-bold"
                >
                  <QrCode size={16} weight="bold" /> Scan to Add Secondary
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Breaks */}
      {isCheckedIn && (
        <div className="space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-widest text-[#666]">{t('breaks')}</h3>
          {activeBreak ? (
            <div className="bg-[#FF8C00]/10 border border-[#FF8C00]/30 p-4">
              <p className="text-sm font-medium">{t('currently_on')}: <strong className="text-[#FF8C00]">{t(activeBreak.break_type)}</strong></p>
              <p className="text-xs text-[#666] mt-1">Started: {new Date(activeBreak.start_time).toLocaleTimeString()}</p>
              <Button
                data-testid="end-break-btn"
                onClick={() => handleAction('break-end')}
                className="mt-3 bg-[#0A0A0A] text-white hover:bg-[#0A0A0A]/90 hover:text-white rounded-full w-full"
              >
                {t('end_break')}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {BREAK_TYPES.map(bt => (
                <button
                  key={bt.key}
                  data-testid={`break-${bt.key}-btn`}
                  onClick={() => handleAction(`break-start-${bt.key}`)}
                  className="bg-white border border-[#E5E5E5] p-4 flex items-center gap-3 hover:border-[#FF8C00] transition-colors min-h-[56px]"
                >
                  <bt.icon size={20} className="text-[#FF8C00]" />
                  <span className="text-sm font-medium">{t(bt.label)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Today's Breaks */}
      {breaks.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-bold uppercase tracking-widest text-[#666]">{t('todays_breaks')}</h3>
          {breaks.map(b => (
            <div key={b.id} className="bg-white border border-[#E5E5E5] p-3 flex items-center justify-between">
              <div>
                <span className="text-xs uppercase tracking-widest font-medium">{t(b.break_type)}</span>
                <p className="text-xs text-[#666]">{new Date(b.start_time).toLocaleTimeString()} - {b.end_time ? new Date(b.end_time).toLocaleTimeString() : 'Ongoing'}</p>
              </div>
              <div className="text-right">
                <span className="text-sm font-medium">{b.duration_minutes ? `${b.duration_minutes} min` : '-'}</span>
                {b.excess_minutes > 0 && <p className="text-xs text-[#FF3333]">+{b.excess_minutes} excess</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Leave Vehicle — at bottom, small, with End-KM capture */}
      {isCheckedIn && (
        <div className="pt-4 border-t border-[#E5E5E5]">
          <button
            data-testid="leave-vehicle-btn"
            onClick={() => { setLeaveKmValue(''); setLeaveKmPhoto(''); setLeaveKmDialog(true); }}
            className="w-full text-center py-2 text-xs uppercase tracking-widest text-[#FF3333] border border-[#E5E5E5] hover:bg-[#FF3333]/5 transition-colors"
          >
            <Truck size={14} className="inline mr-1" /> {t('leave_vehicle')}
          </button>
        </div>
      )}

      {/* End-KM Entry Dialog — mandatory before releasing the vehicle */}
      {leaveKmDialog && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" data-testid="leave-km-dialog">
          <div className="bg-white w-full max-w-sm p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Truck size={20} className="text-[#FF3333]" />
              <p className="text-lg font-bold">Leave Vehicle</p>
            </div>
            <p className="text-xs text-[#666]">Enter the final odometer reading before leaving the vehicle. Photo is optional but recommended.</p>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#666]">End KM *</label>
              <input
                type="number"
                value={leaveKmValue}
                onChange={e => setLeaveKmValue(e.target.value)}
                className="w-full border border-[#E5E5E5] rounded px-3 py-2 text-lg font-bold mt-1"
                placeholder="Odometer reading"
                autoFocus
                data-testid="leave-km-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#666]">Odometer Photo</label>
              {leaveKmPhoto ? (
                <div className="relative mt-1">
                  <img src={leaveKmPhoto} alt="End KM" className="w-full h-32 object-cover rounded border border-[#E5E5E5]" />
                  <button onClick={() => setLeaveKmPhoto('')} className="absolute top-1 right-1 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded" data-testid="leave-km-photo-clear">Retake</button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setLeaveKmCamera(true)}
                  className="w-full mt-1 border-2 border-dashed border-[#E5E5E5] rounded px-3 py-3 text-xs uppercase tracking-widest text-[#666] hover:bg-[#F3F3F3] flex items-center justify-center gap-2"
                  data-testid="leave-km-photo-btn"
                >
                  <Camera size={16} /> Capture Photo
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                onClick={() => setLeaveKmDialog(false)}
                disabled={leaveSubmitting}
                className="py-3 text-sm font-bold uppercase tracking-widest border border-[#E5E5E5] hover:bg-[#F3F3F3] disabled:opacity-50"
                data-testid="leave-km-cancel"
              >
                Cancel
              </button>
              <button
                onClick={submitLeaveWithKm}
                disabled={leaveSubmitting || !leaveKmValue}
                className="py-3 text-sm font-bold uppercase tracking-widest bg-[#FF3333] text-white hover:bg-[#FF3333]/90 disabled:opacity-50"
                data-testid="leave-km-confirm"
              >
                {leaveSubmitting ? 'Leaving…' : 'Confirm Leave'}
              </button>
            </div>
          </div>
        </div>
      )}

      {leaveKmCamera && (
        <CameraCapture
          onCapture={(img) => { setLeaveKmPhoto(img); setLeaveKmCamera(false); }}
          onClose={() => setLeaveKmCamera(false)}
        />
      )}

      {/* Start-KM Entry Dialog — mandatory after scanning the vehicle QR */}
      {scanKmDialog && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" data-testid="scan-km-dialog">
          <div className="bg-white w-full max-w-sm p-5 space-y-4">
            <div className="flex items-center gap-2">
              <QrCode size={20} className="text-[#002FA7]" />
              <p className="text-lg font-bold">Start KM Entry</p>
            </div>
            <p className="text-xs text-[#666]">You just scanned a vehicle QR. Enter the current odometer reading before taking the vehicle. Photo is optional but recommended.</p>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#666]">Start KM *</label>
              <input
                type="number"
                value={scanKmValue}
                onChange={e => setScanKmValue(e.target.value)}
                className="w-full border border-[#E5E5E5] rounded px-3 py-2 text-lg font-bold mt-1"
                placeholder="Odometer reading"
                autoFocus
                data-testid="scan-km-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#666]">Odometer Photo</label>
              {scanKmPhoto ? (
                <div className="relative mt-1">
                  <img src={scanKmPhoto} alt="Start KM" className="w-full h-32 object-cover rounded border border-[#E5E5E5]" />
                  <button onClick={() => setScanKmPhoto('')} className="absolute top-1 right-1 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded" data-testid="scan-km-photo-clear">Retake</button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setScanKmCamera(true)}
                  className="w-full mt-1 border-2 border-dashed border-[#E5E5E5] rounded px-3 py-3 text-xs uppercase tracking-widest text-[#666] hover:bg-[#F3F3F3] flex items-center justify-center gap-2"
                  data-testid="scan-km-photo-btn"
                >
                  <Camera size={16} /> Capture Photo
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                onClick={() => setScanKmDialog(null)}
                disabled={scanSubmitting}
                className="py-3 text-sm font-bold uppercase tracking-widest border border-[#E5E5E5] hover:bg-[#F3F3F3] disabled:opacity-50"
                data-testid="scan-km-cancel"
              >
                Cancel
              </button>
              <button
                onClick={submitScanWithKm}
                disabled={scanSubmitting || !scanKmValue || !(parseInt(scanKmValue, 10) > 0)}
                className="py-3 text-sm font-bold uppercase tracking-widest bg-[#002FA7] text-white hover:bg-[#002FA7]/90 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-[#CCCCCC] disabled:hover:bg-[#CCCCCC]"
                data-testid="scan-km-confirm"
              >
                {scanSubmitting ? 'Assigning…' : 'Confirm & Take Vehicle'}
              </button>
            </div>
          </div>
        </div>
      )}

      {scanKmCamera && (
        <CameraCapture
          onCapture={(img) => { setScanKmPhoto(img); setScanKmCamera(false); }}
          onClose={() => setScanKmCamera(false)}
        />
      )}
    </div>
  );
}

